import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["checkbox", "controlId", "size"];
import PropTypes from 'prop-types';
import React, { cloneElement, useContext, useRef } from 'react';
import { SWITCH_SIZES } from '../../constants';
import CustomPropTypes from '../../util/custom-prop-types';
import { identifier } from '../../util/identifier';
import { Checkbox } from '../Checkbox/Checkbox';
import { FormFieldContext } from '../FormField/FormField.context';
import { SwitchWrapper } from '../SwitchWrapper/SwitchWrapper';
import { StyledSwitch } from './Switch.styles';

/**
 * A switch is on/off control built upon a simple checkbox input.
 *
 * @related ['Checkbox', 'FieldSet', 'LabeledControl']
 * @subcomponents ['SwitchGroup']
 * @since 6.7.0
 */
export var Switch = function Switch(_ref) {
  var checkbox = _ref.checkbox,
    controlId = _ref.controlId,
    size = _ref.size,
    rest = _objectWithoutProperties(_ref, _excluded);
  var context = useContext(FormFieldContext);

  // defaultId is used as the id for the control and the htmlFor for the label
  var defaultId = useRef(identifier()).current;
  var id = checkbox.props.id || controlId || context.controlId || defaultId;
  var disabled = typeof rest.disabled !== 'undefined' ? rest.disabled : context.disabled;
  return /*#__PURE__*/React.createElement(SwitchWrapper, null, /*#__PURE__*/cloneElement(checkbox, {
    id: id,
    offscreen: true,
    disabled: disabled
  }), /*#__PURE__*/React.createElement(StyledSwitch, _extends({
    htmlFor: id
  }, rest, {
    "aria-disabled": disabled,
    size: size || context.size || SWITCH_SIZES.md
  })));
};
Switch.displayName = 'Switch';
Switch.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Identifier used to associate form controls and labels. If this is not defined, an identifier
   * will be automatically generated.
   */
  controlId: PropTypes.string,
  /**
   * A `Checkbox` instance.
   */
  checkbox: CustomPropTypes.instanceOfComponent(Checkbox).isRequired,
  /**
   * The size of the switch, defaults to `SWITCH_SIZES.md`. Can also be inherited from a
   * FieldSet parent.
   */
  size: PropTypes.oneOf(Object.values(SWITCH_SIZES)),
  /**
   * Displays the switch in a disabled state. Can also be inherited from a LabeledControl
   * or FieldSet parent.
   */
  disabled: PropTypes.bool
} : {};
Switch.defaultProps = {
  controlId: undefined,
  disabled: undefined
};